import React, { useState, useEffect, useMemo } from 'react';
import { Save, Sun, Database, MessageCircle, Copy, X, Truck } from 'lucide-react';

const CONFIG_API = `/api/config`;
const READINGS_API = `/api/readings`;

const MorningReadings = () => {
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  
  // Master Config State
  const [tanks, setTanks] = useState([]);
  const [pumps, setPumps] = useState([]);

  const [readings, setReadings] = useState({});
  const [tankReadings, setTankReadings] = useState({});
  const [restockReadings, setRestockReadings] = useState({});
  const [globalTesting, setGlobalTesting] = useState({ Petrol: '', Diesel: '', Xtragreen: '' });
  const [dbData, setDbData] = useState(null);
  
  const [showWhatsappModal, setShowWhatsappModal] = useState(false);
  const [whatsappText, setWhatsappText] = useState('');
  const [isSaved, setIsSaved] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch data when date changes
  useEffect(() => {
    setIsLoading(true);
    
    // Fetch Master Configuration First
    fetch(CONFIG_API, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    })
      .then(res => res.json())
      .then(config => {
        setTanks(config.tanks || []);
        setPumps(config.pumps || []);
        
        // Then fetch daily readings
        return fetch(`${READINGS_API}?date=${date}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
      })
      .then(res => res.json())
      .then(data => {
        if (!data.error) {
          // Populate tank readings
          if (data.today.tanks && Object.keys(data.today.tanks).length > 0) {
            setTankReadings(data.today.tanks);
            setRestockReadings(data.today.restocks || {});
            setIsSaved(true);
          } else {
            setTankReadings({});
            setRestockReadings(data.today.restocks || {});
            setIsSaved(false);
          }

          // Populate nozzle readings
          const newReadings = {};
          if (data.today.nozzles) {
             Object.entries(data.today.nozzles).forEach(([nozzleId, values]) => {
                newReadings[nozzleId] = values.reading;
             });
          }

          setReadings(newReadings);
          setGlobalTesting(data.today.globalTesting || { Petrol: '', Diesel: '', Xtragreen: '' });

          // Save yesterday's data for calculating 24h sales
          setDbData(data.yesterday);
        }
      })
      .catch(err => console.error("Error fetching readings:", err))
      .finally(() => setIsLoading(false));
  }, [date]);

  // Flatten the pump config into a list of nozzles for the table and group by Fuel Type
  const allNozzles = useMemo(() => {
    const flattened = pumps.flatMap(pump => 
      (pump.nozzles || []).map(nozzle => {
        let yesterdayReading = 0;
        if (dbData && dbData.nozzles) {
          const past = dbData.nozzles[nozzle.id];
          if (past) yesterdayReading = parseFloat(past.reading);
        }
        
        return {
          pumpId: pump.id,
          pumpName: pump.pump_name,
          nozzleId: nozzle.id,
          nozzleNumber: nozzle.nozzle_number,
          type: nozzle.fuel_type,
          yesterdayReading,
        };
      })
    );

    // Grouping by Product (Fuel Type) so it's easier for the operator to input
    // Order: Petrol -> Diesel -> Xtragreen
    const typeWeight = { 'Petrol': 1, 'Diesel': 2, 'Xtragreen': 3 };
    
    return flattened.sort((a, b) => {
      const weightA = typeWeight[a.type] || 99;
      const weightB = typeWeight[b.type] || 99;
      
      if (weightA !== weightB) return weightA - weightB;
      
      // If fuel type is the same, sort by Pump Name
      if (a.pumpName < b.pumpName) return -1;
      if (a.pumpName > b.pumpName) return 1;
      
      return 0;
    });
  }, [pumps, dbData]);

  const handleReadingChange = (nozzleId, value) => {
    setIsSaved(false);
    setReadings({ ...readings, [nozzleId]: value });
  };

  const handleGlobalTestingChange = (type, value) => {
    setIsSaved(false);
    setGlobalTesting({ ...globalTesting, [type]: value });
  };

  const handleTankReadingChange = (tankId, value) => {
    setIsSaved(false);
    setTankReadings({ ...tankReadings, [tankId]: value });
  };

  const handleRestockChange = (tankId, value) => {
    setIsSaved(false);
    setRestockReadings({ ...restockReadings, [tankId]: value });
  };

  const handleSave = async () => {
    for (const tank of tanks) {
      if (!tankReadings[tank.id] || tankReadings[tank.id] === '') {
        return alert(`Please enter the Closing Dip for ${tank.tank_name}`);
      }
      if (restockReadings[tank.id] === undefined || restockReadings[tank.id] === '') {
        return alert(`Please enter the Restock Received for ${tank.tank_name}. If none, explicitly enter 0.`);
      }
      const restockVal = parseFloat(restockReadings[tank.id]);
      if (restockVal > 0 && restockVal % 1000 !== 0) {
        return alert(`Restock quantity for ${tank.tank_name} must be a multiple of 1000 (e.g. 4000, 5000). Invalid value: ${restockVal}`);
      }
    }

    const nozzleArray = [];
    allNozzles.forEach(item => {
       const r = readings[item.nozzleId];
       if (r !== undefined && r !== '') {
         nozzleArray.push({
           nozzle_id: item.nozzleId,
           reading: parseFloat(r)
         });
       }
    });

    await fetch(READINGS_API, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify({
        date: date,
        tankReadings: tankReadings,
        restockReadings: restockReadings,
        nozzleReadings: nozzleArray,
        globalTesting: globalTesting
      })
    })
    .then(res => res.json())
    .then(data => {
       if (data.status === 'success') {
         setIsSaved(true);
         alert('Saved successfully!');
       } else {
         alert('Error saving: ' + (data.error || data.message));
       }
    })
    .catch(err => {
      alert('Network error! Please make sure your XAMPP server is running and the backend folder is accessible at http://localhost/backend');
      console.error(err);
    });
  };

  const generateWhatsappReport = () => {
    let msTotal = 0;
    let hsdTotal = 0;
    let xgTotal = 0;

    allNozzles.forEach(item => {
      const todayReading = parseFloat(readings[item.nozzleId]);
      if (!isNaN(todayReading) && todayReading >= item.yesterdayReading) {
        const sale = todayReading - item.yesterdayReading;
        if (item.type === 'Petrol') msTotal += sale;
        else if (item.type === 'Diesel') hsdTotal += sale;
        else if (item.type === 'Xtragreen') xgTotal += sale;
      }
    });

    // Subtract global testing liters to get final net sale
    const msSale = msTotal - parseFloat(globalTesting['Petrol'] || 0);
    const hsdSale = hsdTotal - parseFloat(globalTesting['Diesel'] || 0);
    const xgSale = xgTotal - parseFloat(globalTesting['Xtragreen'] || 0);
    
    const totalSale = msSale + hsdSale + xgSale;

    const selectedDate = new Date(date);
    const yesterdayDate = new Date(selectedDate);
    yesterdayDate.setDate(selectedDate.getDate() - 1);
    
    const formatDate = (d) => `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getFullYear()).slice(2)}`;

    // Build Dynamic Stock section based on Tanks
    let stockSection = '';
    tanks.forEach(tank => {
        const dip = tankReadings[tank.id] || '0';
        stockSection += `${tank.fuel_type === 'Petrol' ? 'MS ' : tank.fuel_type === 'Diesel' ? 'HSD' : 'XG '} (${tank.tank_name}) :- ${dip}\n`;
    });

    const text = `${formatDate(yesterdayDate)}
SALE
MS        :- ${msSale.toFixed(0)}
HSD     :- ${hsdSale.toFixed(0)}
XG.       :- ${xgSale.toFixed(0)}
TOTAL: ${totalSale.toFixed(0)}

${formatDate(selectedDate)}
OPENING STOCK
${stockSection}`;

    setWhatsappText(text);
    setShowWhatsappModal(true);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(whatsappText);
    alert('Copied to clipboard!');
  };

  if (pumps.length === 0 && !isLoading) {
    return (
      <div className="max-w-6xl mx-auto p-12 text-center glass-panel">
        <h2 className="text-xl font-bold text-slate-800">No Pumps Configured</h2>
        <p className="text-slate-500 mt-2 mb-6">You need to configure your tanks and pumps in the Master Configuration before you can take readings.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-6xl mx-auto pb-12">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Dips & Fuel Restock</h1>
          <p className="text-slate-500 text-sm mt-1">Record morning baseline dips, restocks, and daily meter readings.</p>
        </div>
        <div className="flex gap-3 items-center">
          <button 
            onClick={generateWhatsappReport}
            disabled={!isSaved}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors shadow-sm ${
              isSaved 
                ? 'bg-[#25D366] hover:bg-[#1ebd5b] text-white' 
                : 'bg-slate-200 text-slate-400 cursor-not-allowed'
            }`}
          >
            <MessageCircle className="w-4 h-4" /> WhatsApp Report
          </button>
          <button 
            onClick={handleSave}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors shadow-sm ${
              isSaved ? 'bg-emerald-50 text-emerald-600 border border-emerald-200' : 'btn-primary'
            }`}
          >
            {isSaved ? <Save className="w-4 h-4" /> : <Save className="w-4 h-4" />} 
            {isSaved ? 'Saved' : 'Save Baseline'}
          </button>
        </div>
      </div>

      <div className="glass-panel p-6 mb-6 flex items-center gap-4">
        <Sun className="w-8 h-8 text-amber-500 shrink-0" />
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Business Date</label>
          <input 
            type="date" 
            className="input-field max-w-[200px]"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>
        <div className="hidden sm:block ml-auto bg-amber-50 text-amber-700 px-4 py-2 rounded-lg text-sm font-medium border border-amber-200">
          {isLoading ? 'Loading records from database...' : `These readings set the Opening Balance for ${date}`}
        </div>
      </div>

      {/* Fuel Restock Section */}
      <div className="glass-panel overflow-hidden mb-6">
        <div className="bg-slate-50 border-b border-slate-100 p-4">
          <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <Truck className="w-5 h-5 text-indigo-500" /> Fuel Received
          </h2>
        </div>
        <div className="p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {tanks.map(tank => {
            const currentRestock = restockReadings[tank.id] !== undefined ? restockReadings[tank.id] : '';
            const restockVal = parseFloat(currentRestock);
            const isRestockInvalid = currentRestock !== '' && restockVal > 0 && restockVal % 1000 !== 0;
            return (
              <div key={tank.id} className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm relative overflow-hidden">
                <div className={`absolute top-0 left-0 w-1 h-full ${
                  tank.fuel_type === 'Petrol' ? 'bg-orange-500' :
                  tank.fuel_type === 'Diesel' ? 'bg-blue-500' : 'bg-emerald-500'
                }`}></div>
                <div className="flex justify-between items-start mb-4 pl-3">
                  <div>
                    <h3 className="font-bold text-slate-800">{tank.tank_name}</h3>
                    <p className="text-xs font-semibold text-slate-500">{tank.fuel_type}</p>
                  </div>
                </div>
                <div className="pl-3">
                  <label className="block text-xs font-medium text-slate-500 mb-1">Restock Received (Liters)</label>
                  <input 
                    type="number" 
                    className={`input-field font-mono font-bold text-lg ${isRestockInvalid ? 'border-red-300 bg-red-50 focus:border-red-500 text-red-700' : ''}`}
                    placeholder="e.g. 0"
                    value={currentRestock}
                    onChange={(e) => handleRestockChange(tank.id, e.target.value)}
                  />
                  {isRestockInvalid && <p className="text-xs text-red-500 mt-1 font-medium">Must be multiple of 1000</p>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Underground Tanks Section */}
      <div className="glass-panel overflow-hidden mb-6">
        <div className="bg-slate-50 border-b border-slate-100 p-4">
          <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <Database className="w-5 h-5 text-indigo-500" />Tank Stock (Closing Dip)
          </h2>
        </div>
        <div className="p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {tanks.map(tank => {
            const currentReading = tankReadings[tank.id] || '';
            const isValid = currentReading === '' || parseFloat(currentReading) <= tank.capacity;
            return (
              <div key={tank.id} className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm relative overflow-hidden">
                <div className={`absolute top-0 left-0 w-1 h-full ${
                  tank.fuel_type === 'Petrol' ? 'bg-orange-500' :
                  tank.fuel_type === 'Diesel' ? 'bg-blue-500' : 'bg-emerald-500'
                }`}></div>
                <div className="flex justify-between items-start mb-4 pl-3">
                  <div>
                    <h3 className="font-bold text-slate-800">{tank.tank_name}</h3>
                    <p className="text-xs font-semibold text-slate-500">{tank.fuel_type}</p>
                  </div>
                  <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded font-medium">
                    {parseFloat(tank.capacity).toLocaleString()} L
                  </span>
                </div>
                <div className="pl-3">
                  <label className="block text-xs font-medium text-slate-500 mb-1">Closing Dip (Liters)</label>
                  <input
                    type="number"
                    className={`input-field font-mono font-bold text-lg ${!isValid ? 'border-red-300 bg-red-50 focus:border-red-500 text-red-700' : ''}`}
                    placeholder="e.g. 4500"
                    value={currentReading}
                    onChange={(e) => handleTankReadingChange(tank.id, e.target.value)}
                  />
                  {!isValid && <p className="text-xs text-red-500 mt-1 font-medium pl-3">Cannot exceed capacity!</p>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Global Testing Section */}
      <div className="glass-panel overflow-hidden mb-6">
        <div className="bg-slate-50 border-b border-slate-100 p-4">
          <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <Sun className="w-5 h-5 text-amber-500" /> Testing (Liters)
          </h2>
        </div>
        <div className="p-6 grid grid-cols-1 sm:grid-cols-3 gap-6">
          {['Petrol', 'Diesel', 'Xtragreen'].map(type => (
            <div key={type} className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm relative overflow-hidden">
              <div className={`absolute top-0 left-0 w-1 h-full ${
                type === 'Petrol' ? 'bg-orange-500' :
                type === 'Diesel' ? 'bg-blue-500' : 'bg-emerald-500'
              }`}></div>
              <label className="block text-sm font-bold text-slate-700 mb-2 pl-3">{type} Testing</label>
              <div className="pl-3">
                <input 
                  type="number" 
                  className="input-field font-mono font-bold text-lg"
                  placeholder="e.g. 5.0"
                  value={globalTesting[type] || ''}
                  onChange={(e) => handleGlobalTestingChange(type, e.target.value)}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Nozzles Section */}
      <div className="glass-panel overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50 text-slate-600 text-sm border-b border-slate-200">
                <th className="p-4 font-semibold">Pump</th>
                <th className="p-4 font-semibold">Nozzle</th>
                <th className="p-4 font-semibold">Fuel Type</th>
                <th className="p-4 font-semibold">Yesterday's Reading</th>
                <th className="p-4 font-semibold">Today's 6:00 AM Reading</th>
                <th className="p-4 font-semibold text-right">Raw 24h Sales</th>
              </tr>
            </thead>
            <tbody className="text-sm bg-white">
              {allNozzles.map((item) => {
                const todayReadingStr = readings[item.nozzleId];
                const todayReading = parseFloat(todayReadingStr);
                
                const isReadingValid = !isNaN(todayReading) && todayReading >= item.yesterdayReading;
                const sales24h = isReadingValid ? (todayReading - item.yesterdayReading).toFixed(2) : '-';

                return (
                  <tr key={item.nozzleId} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors">
                    <td className="p-4 font-semibold text-slate-900">{item.pumpName}</td>
                    <td className="p-4 font-medium text-slate-700">Nozzle {item.nozzleNumber}</td>
                    <td className="p-4">
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-md ${
                        item.type === 'Petrol' ? 'bg-orange-100 text-orange-700' :
                        item.type === 'Diesel' ? 'bg-blue-100 text-blue-700' :
                        'bg-emerald-100 text-emerald-700'
                      }`}>
                        {item.type}
                      </span>
                    </td>
                    <td className="p-4 text-slate-500 font-mono">
                      {item.yesterdayReading.toFixed(2)}
                    </td>
                    <td className="p-4">
                      <input 
                        type="number" 
                        className={`input-field max-w-[200px] font-mono font-bold ${todayReadingStr && !isReadingValid ? 'border-red-300 focus:border-red-500 bg-red-50' : 'border-primary-200 focus:border-primary-500'}`}
                        placeholder="Enter Reading"
                        value={todayReadingStr || ''}
                        onChange={(e) => handleReadingChange(item.nozzleId, e.target.value)}
                      />
                    </td>
                    <td className="p-4 text-right">
                      {sales24h !== '-' ? (
                        <span className="font-bold text-slate-900 bg-slate-100 px-3 py-1.5 rounded-lg">
                          {sales24h} L
                        </span>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* WhatsApp Modal */}
      {showWhatsappModal && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b border-slate-100">
              <h3 className="font-bold text-slate-800 flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-[#25D366]" /> WhatsApp Summary
              </h3>
              <button 
                onClick={() => setShowWhatsappModal(false)}
                className="p-1.5 text-slate-400 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              <textarea 
                readOnly
                value={whatsappText}
                className="w-full h-64 p-4 bg-slate-50 border border-slate-200 rounded-xl font-mono text-sm resize-none focus:outline-none"
              ></textarea>
              <button 
                onClick={copyToClipboard}
                className="w-full mt-4 flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ebd5b] text-white px-4 py-3 rounded-xl font-bold transition-colors"
              >
                <Copy className="w-5 h-5" /> Copy Message
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MorningReadings;
